import { apiClient } from './requestInterceptor';

export interface Agent {
    agentName: string;
    provider: string;
    model: string;
    createdAt: number;
    updatedAt: number;
}

interface AgentsResponse {
    agents: Agent[];
}

export interface AgentMetrics {
    agentName: string;
    messagesAnswered: number;
    handoffs: number;
    errors: number;
}

interface AgentMetricsResponse {
    agents: AgentMetrics[];
}

export interface AgentDetails {
    agentName: string;
    instructions: string;
    model: string;
    handoffs: string[];
    tools: string[];
}

export async function fetchAgents(userKey: string, projectKey: string): Promise<Agent[]> {
    const response = await apiClient(`/agents?projectKey=${projectKey}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    const data: AgentsResponse = await response.json();
    return data.agents;
}

export async function fetchAgentMetrics(userKey: string, projectKey: string, timeRange: string): Promise<AgentMetrics[]> {
    const response = await apiClient(`/agents/metrics?projectKey=${projectKey}&timeRange=${timeRange}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    const data: AgentMetricsResponse = await response.json();
    return data.agents;
}

export async function fetchAgentDetails(userKey: string, projectKey: string, agentName: string): Promise<AgentDetails> {
    const response = await apiClient(`/agents/${agentName}?projectKey=${projectKey}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    return response.json();
}

export interface MessageChartData {
    timestamp: string;
    user: number;
    assistant: number;
}

export interface HandoffChartData {
    timestamp: string;
    handoffs: number;
}

export interface ToolCallsChartData {
    timestamp: string;
    toolCalls: number;
}

export interface ToolCallsBreakdownData {
    timestamp: string;
    [toolName: string]: string | number;
}

export interface ErrorsChartData {
    timestamp: string;
    errors: number;
}

export interface ErrorsBreakdownData {
    timestamp: string;
    [toolName: string]: string | number;
}

export interface AgentInsightData {
    mostActiveTool: { name: string; count: number };
    mostErroredTool: { name: string; count: number };
    commonError: string;
}

export async function fetchAgentMessageCharts(userKey: string, projectKey: string, agentName: string, timeRange: string): Promise<MessageChartData[]> {
    const response = await apiClient(`/agents/${agentName}/messages-over-time?projectKey=${projectKey}&timeRange=${timeRange}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    return response.json();
}

export async function fetchAgentHandoffCharts(userKey: string, projectKey: string, agentName: string, timeRange: string): Promise<HandoffChartData[]> {
    const response = await apiClient(`/agents/${agentName}/handoffs-over-time?projectKey=${projectKey}&timeRange=${timeRange}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    return response.json();
}

export async function fetchAgentToolCallsCharts(userKey: string, projectKey: string, agentName: string, timeRange: string): Promise<ToolCallsChartData[]> {
    const response = await apiClient(`/agents/${agentName}/tool-calls-over-time?projectKey=${projectKey}&timeRange=${timeRange}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    return response.json();
}

export async function fetchAgentToolCallsBreakdown(userKey: string, projectKey: string, agentName: string, timeRange: string): Promise<ToolCallsBreakdownData[]> {
    const response = await apiClient(`/agents/${agentName}/tool-calls-breakdown-over-time?projectKey=${projectKey}&timeRange=${timeRange}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    return response.json();
}

export async function fetchAgentErrorsCharts(userKey: string, projectKey: string, agentName: string, timeRange: string): Promise<ErrorsChartData[]> {
    const response = await apiClient(`/agents/${agentName}/errors-over-time?projectKey=${projectKey}&timeRange=${timeRange}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    return response.json();
}

export async function fetchAgentErrorsBreakdown(userKey: string, projectKey: string, agentName: string, timeRange: string): Promise<ErrorsBreakdownData[]> {
    const response = await apiClient(`/agents/${agentName}/errors-breakdown-over-time?projectKey=${projectKey}&timeRange=${timeRange}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    return response.json();
}

export async function fetchAgentInsights(userKey: string, projectKey: string, agentName: string, timeRange: string): Promise<AgentInsightData> {
    const response = await apiClient(`/agents/${agentName}/insights?projectKey=${projectKey}&timeRange=${timeRange}`, userKey);
    if (!response.ok) throw new Error('Failed To Retrieve Data');
    return response.json();
}
